import type { Chains } from '@infinex/chain-config';

import type {
  ExternalSigningDevicesNestedById,
  MultiSigWalletId,
  MultiSigWalletsNestedById,
  PrivateKeysNestedById,
  SeedPhrasesNestedById,
  UserChainRuntimeAddress,
  UserChainRuntimeAddressForJoin,
  UserExternalSigningDeviceId,
  UserWalletNested,
  UserWalletsById,
  WalletFromExternalSigningDeviceJoin,
  WalletFromMultiSigJoin,
  WalletFromPrivateKeyJoin,
  WalletFromSeedPhraseJoin,
  WalletsV2Dataset,
} from './types';

import {
  findMainWallet,
  mapChainRuntimeAddressesToChainAddresses,
  type UserChainAddress,
} from '../../../wallets';

type WalletQueryOptions = {
  /** When true, map EOAs to all active EVM chains (not just EIP-7702 chains). Defaults to false. */
  includeEoaOnAllEvmChains?: boolean;
};

/** Extract a UserChainRuntimeAddress from a prefixed join row. */
function craFromJoin(
  wj: UserChainRuntimeAddressForJoin
): UserChainRuntimeAddress {
  return {
    id: wj.chainRuntimeAddressId,
    walletId: wj.chainRuntimeAddressWalletId,
    createdAt: wj.chainRuntimeAddressCreatedAt,
    isCurrent: wj.chainRuntimeAddressIsCurrent,
    chainRuntime: wj.chainRuntimeAddressChainRuntime,
    kind: wj.chainRuntimeAddressKind,
    address: wj.chainRuntimeAddressAddress,
    isPrimary: wj.chainRuntimeAddressIsPrimary,
    derivationPath: wj.chainRuntimeAddressDerivationPath,
    custody: wj.chainRuntimeAddressCustody,
  };
}

/**
 * Converts flat walletsV2 dataset into hierarchical structure:
 * SeedPhrase → m Wallets → m*n ChainRuntimeAddresses
 * PrivateKey → 1 Wallet → 1 ChainRuntimeAddress
 * ExternalSigningDevice → n Wallet → n ChainRuntimeAddress
 *
 * Useful for displaying nested UI like settings pages
 */
export function getWalletsV2NestedRepresentation(
  // TODO multi-wallet: remove `| undefined` from param type
  dataset: WalletsV2Dataset | undefined
): {
  seedPhrases: SeedPhrasesNestedById;
  privateKeys: PrivateKeysNestedById;
  externalSigningDevices: ExternalSigningDevicesNestedById;
  multiSigWallets: MultiSigWalletsNestedById;
} {
  if (!dataset) {
    return {
      seedPhrases: {},
      privateKeys: {},
      externalSigningDevices: {},
      multiSigWallets: {},
    };
  }

  const { custodies, noChildrenRecoveryPhrases } = dataset;

  // Process seed phrase wallets
  const seedPhrases = custodies
    .filter(
      (wj): wj is WalletFromSeedPhraseJoin => wj.groupKind === 'recoveryPhrase'
    )
    .reduce<SeedPhrasesNestedById>((phrases, wj) => {
      // Ensure parent recovery phrase exists
      if (!phrases[wj.recoveryPhraseId]) {
        phrases[wj.recoveryPhraseId] = {
          id: wj.recoveryPhraseId,
          createdAt: wj.recoveryPhraseCreatedAt,
          isCurrent: wj.recoveryPhraseIsCurrent,
          turnkeyWalletId: wj.recoveryPhraseTurnkeyWalletId,
          source: wj.recoveryPhraseSource,
          isImported: wj.recoveryPhraseIsImported,
          label: wj.recoveryPhraseLabel,
          exportedAt: wj.recoveryPhraseExportedAt,
          wallets: {},
        };
      }
      const recoveryPhrase = phrases[wj.recoveryPhraseId];

      // Ensure parent wallet exists in recovery phrase
      if (!recoveryPhrase.wallets[wj.walletId]) {
        // Construct wallet with discriminated union structure
        recoveryPhrase.wallets[wj.walletId] = {
          id: wj.walletId,
          recoveryPhraseId: wj.walletRecoveryPhraseId,
          createdAt: wj.walletCreatedAt,
          isCurrent: wj.walletIsCurrent,
          source: wj.walletSource,
          isImported: wj.walletIsImported,
          seedPhraseIndex: wj.walletSeedPhraseIndex,
          label: wj.walletLabel,
          avatarIcon: wj.walletAvatarIcon,
          avatarColor: wj.walletAvatarColor,
          avatarEmoji: wj.walletAvatarEmoji,
          dappSigningDisabled: wj.walletDappSigningDisabled,
          exportedAt: wj.walletExportedAt,
          custody: wj.chainRuntimeAddressCustody,
          chainRuntimeAddresses: {},
        };
      }
      const wallet = recoveryPhrase.wallets[wj.walletId];

      // Add chain runtime address to wallet
      wallet.chainRuntimeAddresses[wj.chainRuntimeAddressId] = craFromJoin(wj);

      return phrases;
    }, {});

  // Process private key wallets (1:1:1 relationship)
  const privateKeys = custodies
    .filter(
      (wj): wj is WalletFromPrivateKeyJoin => wj.groupKind === 'privateKey'
    )
    .reduce<PrivateKeysNestedById>((acc, wj) => {
      acc[wj.walletId] = {
        wallet: {
          id: wj.walletId,
          createdAt: wj.walletCreatedAt,
          isCurrent: wj.walletIsCurrent,
          source: wj.walletSource,
          isImported: wj.walletIsImported,
          label: wj.walletLabel,
          avatarIcon: wj.walletAvatarIcon,
          avatarColor: wj.walletAvatarColor,
          avatarEmoji: wj.walletAvatarEmoji,
          dappSigningDisabled: wj.walletDappSigningDisabled,
          seedPhraseIndex: null,
          turnkeyPrivateKeyId: wj.walletTurnkeyPrivateKeyId,
          exportedAt: wj.walletExportedAt,
          custody: wj.chainRuntimeAddressCustody,
          chainRuntimeAddresses: {
            [wj.chainRuntimeAddressId]: craFromJoin(wj),
          },
        },
      };

      return acc;
    }, {});

  // Add recovery phrases without wallets
  const recoveryPhrasesWithoutWallets =
    noChildrenRecoveryPhrases.reduce<SeedPhrasesNestedById>((phrases, rp) => {
      phrases[rp.recoveryPhraseId] = {
        id: rp.recoveryPhraseId,
        createdAt: rp.recoveryPhraseCreatedAt,
        isCurrent: rp.recoveryPhraseIsCurrent,
        turnkeyWalletId: rp.recoveryPhraseTurnkeyWalletId,
        source: rp.recoveryPhraseSource,
        isImported: rp.recoveryPhraseIsImported,
        label: rp.recoveryPhraseLabel,
        exportedAt: rp.recoveryPhraseExportedAt,
        wallets: {},
      };
      return phrases;
    }, {});

  // Process external signing devices (grouped by device container, like seed phrases)
  const externalSigningDevices = custodies
    .filter(
      (wj): wj is WalletFromExternalSigningDeviceJoin =>
        wj.groupKind === 'externalSigningDevice'
    )
    .reduce<ExternalSigningDevicesNestedById>((devices, wj) => {
      // Get the device container ID (grouping key)
      const deviceId =
        wj.externalSigningDeviceId as UserExternalSigningDeviceId;

      // Ensure parent device container exists
      if (!devices[deviceId]) {
        devices[deviceId] = {
          id: deviceId,
          createdAt: wj.externalSigningDeviceCreatedAt,
          isCurrent: wj.externalSigningDeviceIsCurrent,
          deviceType: wj.externalSigningDeviceDeviceType,
          label: wj.externalSigningDeviceLabel,
          wallets: {},
        };
      }
      const device = devices[deviceId];

      // Ensure wallet exists in device
      if (!device.wallets[wj.walletId]) {
        device.wallets[wj.walletId] = {
          id: wj.walletId,
          createdAt: wj.walletCreatedAt,
          isCurrent: wj.walletIsCurrent,
          source: wj.walletSource,
          isImported: wj.walletIsImported,
          label: wj.walletLabel,
          avatarIcon: wj.walletAvatarIcon,
          avatarColor: wj.walletAvatarColor,
          avatarEmoji: wj.walletAvatarEmoji,
          dappSigningDisabled: wj.walletDappSigningDisabled,
          seedPhraseIndex: wj.walletSeedPhraseIndex ?? null, // Account index for hardware wallets
          externalSigningDeviceId: deviceId,
          custody: wj.chainRuntimeAddressCustody,
          chainRuntimeAddresses: {},
        };
      }
      const wallet = device.wallets[wj.walletId];

      // Add chain runtime address to wallet
      wallet.chainRuntimeAddresses[wj.chainRuntimeAddressId] = craFromJoin(wj);

      return devices;
    }, {});

  // Process multi-sig wallets (flat: each Safe maps directly to one wallet entry)
  const multiSigWallets = custodies
    .filter(
      (wj): wj is WalletFromMultiSigJoin => wj.groupKind === 'multiSigWallet'
    )
    .reduce<MultiSigWalletsNestedById>((wallets, wj) => {
      const multiSigId = wj.multiSigWalletId as MultiSigWalletId;

      // Ensure flat multi-sig entry exists (combines multi-sig metadata + wallet fields)
      if (!wallets[multiSigId]) {
        wallets[multiSigId] = {
          // Multi-sig metadata (Omit<MultiSigWalletForJoin, 'id'>)
          multiSigType: wj.multiSigWalletMultiSigType,
          address: wj.multiSigWalletAddress,
          deployedChains: wj.multiSigWalletDeployedChains,
          // Wallet fields (UserWalletFromMultiSig)
          id: wj.walletId,
          createdAt: wj.walletCreatedAt,
          isCurrent: wj.walletIsCurrent,
          source: wj.walletSource,
          isImported: wj.walletIsImported,
          label: wj.walletLabel,
          avatarIcon: wj.walletAvatarIcon,
          avatarColor: wj.walletAvatarColor,
          avatarEmoji: wj.walletAvatarEmoji,
          dappSigningDisabled: wj.walletDappSigningDisabled,
          seedPhraseIndex: null,
          multiSigWalletId: multiSigId,
          custody: wj.chainRuntimeAddressCustody,
          chainRuntimeAddresses: {},
        };
      }
      const entry = wallets[multiSigId];

      // Add chain runtime address
      entry.chainRuntimeAddresses[wj.chainRuntimeAddressId] = craFromJoin(wj);

      return wallets;
    }, {});

  return {
    seedPhrases: {
      ...seedPhrases,
      ...recoveryPhrasesWithoutWallets,
    },
    privateKeys,
    externalSigningDevices,
    multiSigWallets,
  };
}

/**
 * Maps walletsV2 dataset to top-level wallets (not nested under parent seed phrases, private keys, or external signing devices)
 *
 * Each wallet contains 1 or more chain runtime addresses (EVM, Solana, maybe Sui)
 */
export function getWalletsV2Wallets(
  dataset: WalletsV2Dataset
): UserWalletsById {
  return dataset.custodies.reduce<UserWalletsById>((wallets, wj) => {
    if (!wallets[wj.walletId]) {
      switch (wj.groupKind) {
        case 'recoveryPhrase':
          wallets[wj.walletId] = {
            id: wj.walletId,
            recoveryPhraseId: wj.walletRecoveryPhraseId,
            createdAt: wj.walletCreatedAt,
            isCurrent: wj.walletIsCurrent,
            source: wj.walletSource,
            seedPhraseIndex: wj.walletSeedPhraseIndex,
            isImported: wj.walletIsImported,
            label: wj.walletLabel,
            avatarIcon: wj.walletAvatarIcon,
            avatarColor: wj.walletAvatarColor,
            avatarEmoji: wj.walletAvatarEmoji,
            dappSigningDisabled: wj.walletDappSigningDisabled,
            exportedAt: wj.walletExportedAt,
            custody: wj.chainRuntimeAddressCustody,
            chainRuntimeAddresses: {},
          };
          break;
        case 'externalSigningDevice':
          wallets[wj.walletId] = {
            id: wj.walletId,
            createdAt: wj.walletCreatedAt,
            isCurrent: wj.walletIsCurrent,
            source: wj.walletSource,
            isImported: wj.walletIsImported,
            label: wj.walletLabel,
            avatarIcon: wj.walletAvatarIcon,
            avatarColor: wj.walletAvatarColor,
            avatarEmoji: wj.walletAvatarEmoji,
            dappSigningDisabled: wj.walletDappSigningDisabled,
            seedPhraseIndex: wj.walletSeedPhraseIndex ?? null,
            externalSigningDeviceId: wj.walletExternalSigningDeviceId,
            custody: wj.chainRuntimeAddressCustody,
            chainRuntimeAddresses: {},
          };
          break;
        case 'multiSigWallet':
          wallets[wj.walletId] = {
            id: wj.walletId,
            createdAt: wj.walletCreatedAt,
            isCurrent: wj.walletIsCurrent,
            source: wj.walletSource,
            isImported: wj.walletIsImported,
            label: wj.walletLabel,
            seedPhraseIndex: null,
            multiSigWalletId: wj.walletMultiSigWalletId,
            multiSigType: wj.multiSigWalletMultiSigType,
            address: wj.multiSigWalletAddress,
            deployedChains: wj.multiSigWalletDeployedChains,
            avatarIcon: wj.walletAvatarIcon,
            avatarColor: wj.walletAvatarColor,
            avatarEmoji: wj.walletAvatarEmoji,
            dappSigningDisabled: wj.walletDappSigningDisabled,
            custody: wj.chainRuntimeAddressCustody,
            chainRuntimeAddresses: {},
          };
          break;
        case 'privateKey':
          wallets[wj.walletId] = {
            id: wj.walletId,
            createdAt: wj.walletCreatedAt,
            isCurrent: wj.walletIsCurrent,
            source: wj.walletSource,
            isImported: wj.walletIsImported,
            label: wj.walletLabel,
            avatarIcon: wj.walletAvatarIcon,
            avatarColor: wj.walletAvatarColor,
            avatarEmoji: wj.walletAvatarEmoji,
            dappSigningDisabled: wj.walletDappSigningDisabled,
            seedPhraseIndex: null,
            turnkeyPrivateKeyId: wj.walletTurnkeyPrivateKeyId,
            exportedAt: wj.walletExportedAt,
            custody: wj.chainRuntimeAddressCustody,
            chainRuntimeAddresses: {},
          };
          break;
        default:
          wj satisfies never;
      }
    }

    wallets[wj.walletId].chainRuntimeAddresses[wj.chainRuntimeAddressId] =
      craFromJoin(wj);

    return wallets;
  }, {});
}

/**
 * Maps the chain runtime addresses stored in the DB to the chain-level addresses with backwards compatible flags (`isLegacy`).
 * - For EVM, there's some complexity due to not every chain being 7702-enabled:
 *   - Infinex-generated SCAs are mapped to every active EVM chain, `isLegacy` if chain is 7702-enabled (because there will be an EOA).
 *   - Imported SCAs (e.g. Safe multi-sig) are never legacy — they are permanent, not being replaced by an EOA.
 *   - EOAs to every 7702-enabled chain, is legacy always false.
 * - For non-EVM, simple, just map to an EOA on every chain for the runtime. Import status doesn't matter, and `isLegacy` always false.
 *
 * Note: Excludes near intents deposit addresses which are fetched on the frontend and not stable in the DB
 * @returns Chain-level addresses
 */
export function getWalletsV2MappedChainAddresses({
  walletsV2,
  activeChains,
  options: { includeEoaOnAllEvmChains = false },
}: {
  walletsV2: WalletsV2Dataset;
  activeChains: Partial<Chains>;
  options: WalletQueryOptions;
}): UserChainAddress[] {
  const userChainRuntimeAddresses = walletsV2.custodies.map((wj) => ({
    chainRuntime: wj.chainRuntimeAddressChainRuntime,
    address: wj.chainRuntimeAddressAddress,
    kind: wj.chainRuntimeAddressKind,
    isPrimary: wj.chainRuntimeAddressIsPrimary,
    walletId: wj.walletId,
    id: wj.chainRuntimeAddressId,
    createdAt: wj.chainRuntimeAddressCreatedAt,
    isCurrent: wj.chainRuntimeAddressIsCurrent,
    label: wj.walletLabel,
    isImported: wj.walletIsImported,
    custody: wj.chainRuntimeAddressCustody,
  }));

  return mapChainRuntimeAddressesToChainAddresses({
    userChainRuntimeAddresses,
    activeChains,
    includeEoaOnAllEvmChains: includeEoaOnAllEvmChains ?? false,
  });
}

/**
 * Find the wallet auto-created on sign up
 * @returns
 * - `null` right after sign up, which is a valid state because we want to allow the user to explore the app even before their wallet is asynchronously created in the background
 * - In all other cases, the main wallet
 */
export function getWalletsV2MainWallet(
  walletsV2: WalletsV2Dataset
): UserWalletNested | null {
  const allWallets = Object.values(getWalletsV2Wallets(walletsV2));

  return findMainWallet(allWallets);
}

// TODO multi-wallet after the biz model/logic stabilize, a quick set of unit tests wouldn't hurt (can be trivial using snapshots)
